import React, { useCallback, useContext, useEffect, useState } from "react";
import { getData } from "../core/api/rest-api";
import Grid from "@mui/material/Grid";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { IProviders } from "../core/interface/general/IGeneral";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import AppContext, { AppContextType } from "../core/context/auth-context";
import { exchangeProviderToken } from "../core/service/provider-token-service";

const Providers = () => {
  // null = still loading; [] = loaded but empty; loadError distinguishes a fetch failure.
  const [providerList, setProviderList] = useState<IProviders[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [selectingId, setSelectingId] = useState<string | null>(null);
  const { setAppContext } = useContext(AppContext) as AppContextType;
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const fetchProviders = useCallback(async () => {
    setLoadError(false);
    setProviderList(null);
    try {
      const data = await (await getData("/providers")).data;
      setProviderList(data);
    } catch (e) {
      console.log(e);
      setLoadError(true);
      setProviderList([]);
    }
  }, []);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const selectProvider = async (provider: IProviders) => {
    if (selectingId) return;
    setSelectingId(provider.id);
    try {
      const { adminToken, careTeams } = await exchangeProviderToken(provider.id);
      sessionStorage.setItem("providerName", provider.name);
      setAppContext({
        selectedProviderId: provider.id,
        authToken: adminToken,
        careTeams,
      });
      navigate("/admin/overview");
    } catch (error) {
      console.error("Provider token exchange failed:", error);
      enqueueSnackbar("Could not open this provider. Please try again.", { variant: "error" });
      setSelectingId(null);
    }
  };

  const loading = providerList === null;

  return (
    <Box sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ mb: 0.5 }}>
        Select a provider
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Choose the provider whose practitioners and content you want to manage.
      </Typography>

      {loadError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={fetchProviders}>
              Retry
            </Button>
          }
        >
          Could not load providers. Check your connection and try again.
        </Alert>
      )}

      <Grid container spacing={2.5}>
        {loading &&
          Array.from({ length: 8 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
              <Skeleton variant="rounded" height={168} sx={{ borderRadius: "14px" }} />
            </Grid>
          ))}

        {!loading &&
          providerList!.map((item: IProviders) => {
            const busy = selectingId === item.id;
            const dimmed = selectingId !== null && !busy;
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                <Card
                  sx={{
                    height: "100%",
                    opacity: dimmed ? 0.5 : 1,
                    transition: "border-color .18s ease, box-shadow .18s ease, transform .18s ease",
                    "&:hover": selectingId
                      ? {}
                      : {
                          borderColor: "primary.main",
                          boxShadow: (t) => `0 10px 28px ${alpha(t.palette.primary.main, 0.14)}`,
                          transform: "translateY(-3px)",
                        },
                  }}
                >
                  <CardActionArea
                    onClick={() => selectProvider(item)}
                    disabled={selectingId !== null}
                    sx={{ height: "100%", p: 2 }}
                  >
                    <Stack spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          height: 92,
                          borderRadius: "12px",
                          bgcolor: "grey.50",
                          border: "1px solid",
                          borderColor: "divider",
                          display: "grid",
                          placeItems: "center",
                          overflow: "hidden",
                          p: 1.5,
                        }}
                      >
                        <Box
                          component="img"
                          src={item.logoUrl}
                          alt={item.name}
                          sx={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            borderRadius: 1,
                          }}
                        />
                        {busy && (
                          <Box
                            sx={{
                              position: "absolute",
                              inset: 0,
                              display: "grid",
                              placeItems: "center",
                              bgcolor: (t) => alpha(t.palette.background.paper, 0.7),
                            }}
                          >
                            <CircularProgress size={26} />
                          </Box>
                        )}
                      </Box>
                      <Typography
                        variant="subtitle1"
                        align="center"
                        sx={{ fontWeight: 600, lineHeight: 1.25 }}
                      >
                        {item.name}
                      </Typography>
                    </Stack>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
      </Grid>

      {!loading && !loadError && providerList!.length === 0 && (
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          No providers are available for your account.
        </Typography>
      )}
    </Box>
  );
};

export default Providers;
